import React, { useState, useEffect } from 'react';
import { Paper, Typography, TextField, Grid, FormControl, Autocomplete, Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import axios from 'axios';
import SavedPopup from './SavedPopup';
import GmailAccountManager from './GmailAccountManager';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SettingsIcon from '@mui/icons-material/Settings';
import { Switch, IconButton, Collapse } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

const BarterDealAutomation = () => {
  const [automations, setAutomations] = useState([]);
  const [selectedAutomation, setSelectedAutomation] = useState(null);
  const [newAutomationDialog, setNewAutomationDialog] = useState(false);
  const [newAutomationName, setNewAutomationName] = useState('');
  const [dialogMode, setDialogMode] = useState('new'); // 'new' or 'edit'
  const [automationDialog, setAutomationDialog] = useState(false);
  const [automationName, setAutomationName] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [enabled, setEnabled] = useState(true);

  const [campaign, setCampaign] = useState({
    _id: '',
    emailTemplates: {
      afterCareEmail: {
        continueTemplate: '',
        notContinueTemplate: ''
      },
      orderEmail: '',
      notPostedEmail: '',
      briefingEmail: '',
      rebookingEmail: ''
    },
    postingDaysAfterBriefing: '',
    notOrderedDaysAfterBriefing: '',
    revenue: '',
    gmailAccount: '',
    dataSheet: '',
    contactSheet: '',
    clickUpSheetName: '',
    googleSheetId: ''
  });
  const [templates, setTemplates] = useState([]);
  const [openRevenueDialog, setOpenRevenueDialog] = useState(false);
  const [tempRevenue, setTempRevenue] = useState(null);
  const [openSavedPopup, setOpenSavedPopup] = useState(false);
  const [loading, setLoading] = useState(true);
  const [emailAccounts, setEmailAccounts] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await Promise.all([fetchTemplates(), fetchAutomations(), fetchEmailAccounts()]);
      setLoading(false);
    };
    fetchData();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/email-templates`);
      setTemplates(response.data || []);
    } catch (error) {
      console.error('Failed to fetch email templates:', error);
    }
  };

  const fetchAutomations = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/barterdeal-automation`);
      console.log('Fetched automations:', response.data);
      setAutomations(response.data || []);
      if (response.data && response.data.length > 0) {
        setSelectedAutomation(response.data[0]);
        setCampaign(response.data[0]);
        setEnabled(response.data[0].status === 'active');
      }
    } catch (error) {
      console.error('Failed to fetch automations:', error);
    }
  };

  const fetchEmailAccounts = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/gmail-accounts`);
      console.log('Email accounts:', response.data);
      setEmailAccounts(response.data || []);
    } catch (error) {
      console.error('Failed to fetch email accounts:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setCampaign(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEmailTemplateChange = (field, value) => {
    setCampaign(prev => ({
      ...prev,
      emailTemplates: {
        ...prev.emailTemplates,
        [field]: value
      }
    }));
  };

  const handleAfterCareEmailChange = (field, value) => {
    setCampaign(prev => ({
      ...prev,
      emailTemplates: {
        ...prev.emailTemplates,
        afterCareEmail: {
          ...prev.emailTemplates.afterCareEmail,
          [field]: value
        }
      }
    }));
  };

  const handlePostingDaysChange = (field, value) => {
    setCampaign(prev => ({
      ...prev,
      postingDaysAfterBriefing: {
        ...prev.postingDaysAfterBriefing,
        [field]: value
      }
    }));
  };

  const handleOpenRevenueDialog = () => {
    setTempRevenue(campaign.revenue);
    setOpenRevenueDialog(true);
  };

  const handleCloseRevenueDialog = () => {
    setOpenRevenueDialog(false);
  };

  const handleSaveRevenue = () => {
    handleInputChange('revenue', tempRevenue);
    setOpenRevenueDialog(false);
  };

  const handleSave = async () => {
    try {
      setError(null);
      const response = await axios.put(`${process.env.REACT_APP_API_URL}/barterdeal-automation/${campaign._id}`, campaign);
      if (response.data) {
        setOpenSavedPopup(true);
      } else {
        console.error('Failed to save campaign settings. Please try again.');
      }
    } catch (error) {
      console.error('Failed to save campaign settings:', error);
      setError('Failed to save settings. Please try again.');
    }
  };

  const handleCloseSavedPopup = () => {
    setOpenSavedPopup(false);
  };

  const handleOpenAutomationDialog = (mode, automation = null) => {
    setDialogMode(mode);
    setAutomationName(mode === 'edit' ? automation.name : '');
    setAutomationDialog(true);
  };

  const handleSaveAutomation = async () => {
    try {
      if (dialogMode === 'new') {
        const response = await axios.post(`${process.env.REACT_APP_API_URL}/barterdeal-automation`, {
          automationName: automationName,
          emailTemplates: {
            afterCareEmail: { continueTemplate: '', notContinueTemplate: '' },
            orderEmail: '',
            notPostedEmail: '',
            briefingEmail: '',
            rebookingEmail: ''
          },
          postingDaysAfterBriefing: null,
          notOrderedDaysAfterBriefing: null,
          revenue: null,
          dataSheet: null,
          clickUpSheetName: null,
          googleSheetId: null
        });

        setAutomations([...automations, response.data]);
        setSelectedAutomation(response.data);
        setCampaign(response.data);
      } else {
        // Update existing automation name
        const response = await axios.put(`${process.env.REACT_APP_API_URL}/barterdeal-automation/${campaign._id}`, {
          ...campaign,
          name: automationName
        });

        const updatedAutomations = automations.map(auto =>
          auto._id === campaign._id ? { ...auto, name: automationName } : auto
        );
        setAutomations(updatedAutomations);
        setSelectedAutomation({ ...campaign, name: automationName });
        setCampaign({ ...campaign, name: automationName });
      }

      setAutomationDialog(false);
      setAutomationName('');
    } catch (error) {
      console.error(`Failed to ${dialogMode} automation:`, error);
      setError(`Failed to ${dialogMode} automation. Please try again.`);
    }
  };

  const handleToggleActive = async (automationId, currentStatus) => {
    try {
      const newStatus = currentStatus ? 'paused' : 'active';
      const response = await axios.put(`${process.env.REACT_APP_API_URL}/barterdeal-automation/${automationId}`, {
        status: newStatus
      });
      if (response.data) {
        setAutomations(automations.map(auto =>
          auto._id === automationId ? { ...auto, status: newStatus } : auto
        ));
      }
    } catch (error) {
      console.error('Failed to update automation status:', error);
    }
  };

  const handleDelete = async (automationId) => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/barterdeal-automation/${automationId}`);
      // Remove from automations list
      const updatedAutomations = automations.filter(auto => auto._id !== automationId);
      setAutomations(updatedAutomations);
      if (updatedAutomations.length > 0) {
        setSelectedAutomation(updatedAutomations[0]);
        setCampaign(updatedAutomations[0]);
      }
    } catch (error) {
      console.error('Failed to delete automation:', error);
    }
  };

  const renderTemplateSelect = (label, value, onChange, helperText) => (
    <FormControl fullWidth sx={{ mb: 2 }}>
      <Autocomplete
        value={templates.find(t => t.name === value) || null}
        onChange={(event, newValue) => onChange(newValue?.name || '')}
        options={templates}
        getOptionLabel={(option) => option.name || ''}
        renderInput={(params) => <TextField {...params} label={label} />}
        isOptionEqualToValue={(option, value) => option.name === value?.name}
        loading={loading}
        loadingText="Loading templates..."
      />
      <Typography variant="caption" sx={{ mt: 0.5, ml: 1 }}>{helperText}</Typography>
    </FormControl>
  );

  const renderAutomations = () => {
    return automations.map(automation => (
      <Paper elevation={0} sx={{ p: 2, mb: 2 }} key={automation._id}>
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={12} sm={8}>
            <Typography variant="h6" onClick={() => handleOpenAutomationDialog('edit', automation)}>
              {automation.automationName || 'Unnamed Automation'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4} container justifyContent="flex-end" spacing={1}>
            <Grid item>
              <Switch
                checked={automation.status === 'active'}
                onChange={() => handleToggleActive(automation._id, automation.status === 'active')}
              />
            </Grid>
            <Grid item>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  if (selectedAutomation?._id === automation._id) {
                    // If clicking the same automation's settings, toggle expanded state
                    setExpanded(!expanded);
                  } else {
                    // If clicking a different automation's settings, select it and expand
                    setSelectedAutomation(automation);
                    setCampaign(automation);
                    setExpanded(true);
                  }
                }}
              >
                <SettingsIcon />
              </IconButton>
            </Grid>
            <Grid item>
              <IconButton onClick={() => handleDelete(automation._id)} color="error">
                <DeleteIcon />
              </IconButton>
            </Grid>
          </Grid>
        </Grid>

        <Collapse in={expanded && selectedAutomation?._id === automation._id}>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            {/* Gmail Account Selection */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Autocomplete
                  value={emailAccounts.find(account => account._id === campaign.gmailAccount) || null}
                  onChange={(event, newValue) => handleInputChange('gmailAccount', newValue?._id || '')}
                  options={emailAccounts}
                  getOptionLabel={(option) => `${option.name || ''} (${option.email || ''})`}
                  renderInput={(params) => <TextField {...params} label="Gmail Account" />}
                />
              </FormControl>
            </Grid>

            {/* Posting Check Settings */}
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Check Posting After Days"
                value={campaign.postingDaysAfterBriefing || ''}
                onChange={(e) => handleInputChange('postingDaysAfterBriefing', e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Revenue (€)"
                value={campaign.revenue || ''}
                onChange={(e) => handleInputChange('revenue', e.target.value)}
              />
            </Grid>

            {/* Email Templates */}
            <Grid item xs={12}>
              {renderTemplateSelect(
                "Not Ordered Email Template",
                campaign.emailTemplates?.orderEmail || '',
                (value) => handleEmailTemplateChange('orderEmail', value)
              )}
            </Grid>

            <Grid item xs={12}>
              {renderTemplateSelect(
                "Not Posted Email Template",
                campaign.emailTemplates?.notPostedEmail || '',
                (value) => handleEmailTemplateChange('notPostedEmail', value)
              )}
            </Grid>
            <Grid item xs={12}>
              {renderTemplateSelect(
                "After-Care Continue Email Template",
                campaign.emailTemplates?.afterCareEmail?.continueTemplate || '',
                (value) => handleAfterCareEmailChange('continueTemplate', value)
              )}
            </Grid>
            <Grid item xs={12}>
              {renderTemplateSelect(
                "After-Care Not Continue Email Template",
                campaign.emailTemplates?.afterCareEmail?.notContinueTemplate || '',
                (value) => handleAfterCareEmailChange('notContinueTemplate', value)
              )}
            </Grid>
            <Grid item xs={12}>
              {renderTemplateSelect(
                "Briefing Email Template",
                campaign.emailTemplates?.briefingEmail || '',
                (value) => handleEmailTemplateChange('briefingEmail', value)
              )}
            </Grid>
            <Grid item xs={12}>
              {renderTemplateSelect(
                "Rebooking Email Template",
                campaign.emailTemplates?.rebookingEmail || '',
                (value) => handleEmailTemplateChange('rebookingEmail', value)
              )}
            </Grid>

            {/* Integration Settings */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Data Sheet Name"
                value={campaign.dataSheet || ''}
                onChange={(e) => handleInputChange('dataSheet', e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Contact Sheet Name"
                value={campaign.contactSheet || ''}
                onChange={(e) => handleInputChange('contactSheet', e.target.value)}
                helperText="Name of the sheet where contact information is stored"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="ClickUp List Name"
                value={campaign.clickUpSheetName || ''}
                onChange={(e) => handleInputChange('clickUpSheetName', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Google Sheet ID"
                value={campaign.googleSheetId || ''}
                onChange={(e) => handleInputChange('googleSheetId', e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <Button variant="contained" onClick={handleSave}>
                Save Settings
              </Button>
            </Grid>
          </Grid>
        </Collapse>
      </Paper>
    ));
  };

  const renderAutomationDialog = () => (
    <Dialog
      open={automationDialog}
      onClose={() => setAutomationDialog(false)}
      PaperProps={{
        sx: { minWidth: '400px' }
      }}
    >
      <DialogTitle>
        {dialogMode === 'new' ? 'Create New Automation' : 'Edit Automation Name'}
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Automation Name"
          fullWidth
          value={automationName || ''}
          onChange={(e) => setAutomationName(e.target.value)}
          sx={{ mt: 2 }}
          placeholder="Enter automation name"
          helperText={dialogMode === 'new' ? "Create a unique name for your new automation" : "Update the automation name"}
        />
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={() => setAutomationDialog(false)}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSaveAutomation}
          variant="contained"
          disabled={!automationName?.trim()}
        >
          {dialogMode === 'new' ? 'Create' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <>
      {loading ? (
        <Typography>Loading...</Typography>
      ) : (
        <div style={{ maxWidth: '650px', margin: '0 auto' }}>
          <Paper elevation={0} sx={{ p: 2, mb: 3 }}>
            <Grid container alignItems="center" justifyContent="space-between" spacing={2}>
              <Grid item xs={12} sm>
                <Typography variant="h5">
                  Barter Deal Automation
                </Typography>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenAutomationDialog('new')}
                >
                  Add new
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {renderAutomations()}

          {renderAutomationDialog()}
          <SavedPopup open={openSavedPopup} onClose={handleCloseSavedPopup} />
          {error && (
            <Typography color="error" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}
        </div>
      )}
    </>
  );
};

export default BarterDealAutomation;